<script setup lang="ts">
import {CurrencyDisplay, formatting, Models, useOrderViewContext} from "@hantera/portal-app";
import {Expandable, Panel} from "@hantera/design-system";
import {computed, ref} from "vue";
import InvoiceLine from "./InvoiceLine.vue";
import Capture from "./Capture.vue";
import Decimal from "decimal.js";

const props = withDefaults(defineProps<{
  invoice: Models.Graph.Invoice
  expanded?: boolean
}>(), {
  expanded: true
});

const orderViewContext = useOrderViewContext();

const expanded = ref(props.expanded);

const state = computed(() => {
  
  if (props.invoice.isCancelled) {
    return {
      code: 'cancelled',
      label: 'Cancelled',
    }
  }
  
  const captured = props.invoice.paymentCaptures.reduce((res, i) => {
    return res.add(i.capturedAmount);
  }, new Decimal(0))
  
  if (captured.lt(props.invoice.invoiceTotal)) {
    return {
      code: 'unpaid',
      label: 'Unpaid',
    }
  }

  return {
    code: 'paid',
    label: 'Paid',
  }
  
});

const sortedLines = computed(() => {
  return [...props.invoice.invoiceLines].sort((a,b) => a.invoiceLineNumber.sub(b.invoiceLineNumber).toNumber());
})

const sortedJournalEntries = computed(() => {
  return [...props.invoice.orderJournalEntries].sort((a,b) => {
    let result = a.timestamp.getTime() - b.timestamp.getTime(); 
    
    if (result === 0) {
      result = typeWeight(a.orderJournalEntryType) - typeWeight(b.orderJournalEntryType)
    }
    
    return result;
    
    function typeWeight(type: string) {
      if (type === 'receivable') return 1;
      if (type === 'capture') return 2;
      if (type === 'clearing') return 3;
      if (type === 'fee') return 4;
      return 0;
    }
  });
})

function debit(entry: Models.Graph.OrderJournalEntry) {
  if (entry.orderJournalEntryType === 'receivable') {
    if (entry.amount.gt(0)) {
      return formatting.currency(entry.amount, orderViewContext.state.currencyCode)
    }
  }
  if (entry.orderJournalEntryType === 'capture') {
    if (entry.amount.lt(0)) {
      return formatting.currency(-entry.amount, orderViewContext.state.currencyCode)
    }
  }
}

function credit(entry: Models.Graph.OrderJournalEntry) {
  if (entry.orderJournalEntryType === 'receivable') {
    if (entry.amount.lt(0)) {
      return formatting.currency(-entry.amount, orderViewContext.state.currencyCode)
    }
  }
  if (entry.orderJournalEntryType === 'capture') {
    if (entry.amount.gt(0)) {
      return formatting.currency(entry.amount, orderViewContext.state.currencyCode)
    }
  }
}

</script>

<template>
  <Panel class="invoice light:box-pop">
    <Expandable v-model="expanded">
      <template v-slot:label>
        <div class="invoice-header">
    
          <div
            class="invoice__state"
            :style="`background-color: var(--state-colors__invoice-${state.code})`"
            :title="state.label"
          ></div>
          <div class="invoice__header-details">
            <div class="invoice__title">
              <div
                class="invoice__number"
              >{{ invoice.invoiceNumber }}</div>
              <div
                class="invoice__date"
              >{{ formatting.date(invoice.createdAt) }}</div>
              <div
                class="invoice__total"
              >{{ formatting.currency(invoice.invoiceTotal, orderViewContext.state.currencyCode, CurrencyDisplay.none) }}</div>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:default>
        <div class="invoice-data">
          <div class="invoice-details">
            <div class="invoice-details1">
              <div class="box-content-label">
                Sold to
              </div>
              <div class="invoice-address box-content p">
                <div>
                  <div class="invoice-address-label">Name</div>
                  {{ invoice.invoiceRecipient.name }}
                </div>
                <div>
                  <div class="invoice-address-label">Address</div>
                  <div>{{ invoice.invoiceRecipient.addressLine1 }}</div>
                  <div>{{ invoice.invoiceRecipient.addressLine2 }}</div>
                  <div>
                    {{ invoice.invoiceRecipient.postalCode }} {{ invoice.invoiceRecipient.city }}<span v-if="invoice.invoiceRecipient.state">, {{
                      invoice.invoiceRecipient.state }}</span>
                  </div>
                  <div>{{ formatting.country(invoice.invoiceRecipient.countryCode) }}</div>
                </div>
                <div>
                  <div class="invoice-address-label">E-mail</div>
                  {{ invoice.invoiceRecipient.email }}
                </div>
                <div>
                  <div class="invoice-address-label">Phone</div>
                  {{ invoice.invoiceRecipient.phone }}
                </div>
                <div v-if="invoice.invoiceRecipient.taxId">
                  <div class="invoice-address-label">Tax ID</div>
                  {{ invoice.invoiceRecipient.taxId }}
                </div>
              </div>
            </div>
            <div class="invoice-details2">

              <div v-if="invoice.paymentCaptures.length > 0">
                <div class="box-content-label">
                  Payment Captures
                </div>
                <div class="payment-captures">
                  <Capture
                    v-for="capture in invoice.paymentCaptures"
                    :key="capture.paymentCaptureId"
                    :capture="capture"
                  />
                </div>
              </div>

              <div v-if="sortedJournalEntries.length > 0">
                <div class="box-content-label">
                  Journal
                </div>
                <div class="invoice-journal">
                  <div class="box-content p invoice-journal-entries">
  
                    <div
                      v-for="entry in sortedJournalEntries"
                      :key="entry.orderJournalEntryId"
                      class="invoice-journal-entry"
                    >
                      <div class="invoice-journal__cell">{{formatting.dateTime(entry.timestamp)}}</div>
                      <div class="invoice-journal__cell">{{entry.orderJournalEntryType}}</div>
                      <div class="invoice-journal__cell">{{debit(entry)}}</div>
                      <div class="invoice-journal__cell">{{credit(entry)}}</div>
                    </div>
                  </div>
                </div>
              </div>
              

            </div>
          </div>
          
          <div class="invoice-lines">
            <div class="invoice-lines__header">
              <div class="invoice-line__cell">
                #
              </div>
              <div class="invoice-line__cell">
                Product Number
              </div>
              <div class="invoice-line__cell invoice-line__description">
                Description
              </div>
              <div class="invoice-line__cell">
                Quantity
              </div>
              <div class="invoice-line__cell">
                Net
              </div>
              <div class="invoice-line__cell">
                Tax
              </div>
              <div class="invoice-line__cell">
                Sum
              </div>
            </div>
            <div class="box-content p invoice-lines__lines">
              
              <InvoiceLine
                v-for="line in sortedLines"
                :key="line.invoiceLineId"
                :invoice-line="line"
              />
              
            </div>
          </div>
          
          <div class="invoice-summary">
            <div class="invoice-summary__label invoice-summary__net">Net:</div>
            <div class="invoice-summary__value invoice-summary__net">{{formatting.currency(invoice.invoiceNetTotal, orderViewContext.state.currencyCode, CurrencyDisplay.none)}}</div>
            <div class="invoice-summary__label invoice-summary__tax">Tax:</div>
            <div class="invoice-summary__value invoice-summary__tax">{{formatting.currency(invoice.invoiceTaxTotal, orderViewContext.state.currencyCode, CurrencyDisplay.none)}}</div>
            <div class="invoice-summary__label invoice-summary__total">Grand Total:</div>
            <div class="invoice-summary__value invoice-summary__total">{{formatting.currency(invoice.invoiceTotal, orderViewContext.state.currencyCode, CurrencyDisplay.none)}}</div>
          </div>
        </div>
        
      </template>

    </Expandable>
  </Panel>
</template>

<style scoped>
h3 {
  margin: 5px 0;
}

.invoice {
  max-width: 800px;
}

.invoice :deep(.expandable__label) {
  padding: 0;
}

.invoice-header {
  position: relative;
  padding-left: 10px;
  display: flex;
  align-items: center;
}

.invoice__state {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  border-radius: 5px;
}

.invoice__title {
  display: grid;
  grid-template-columns: min-content min-content 1fr;
}

.invoice__total {
  text-align: right;
}

.invoice-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.invoice-details2 {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.invoice-address {
  margin-bottom: 5px;
}

.invoice-address-label {
  font-size: 12px;
}

.invoice-address, .invoice-contact {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.payment-captures {
  display: grid;
  grid-auto-flow: row;
  gap: 5px;
}

.invoice-journal {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: max-content auto max-content max-content;
  column-gap: 10px;
}

.invoice-journal-entries {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.invoice-journal-entry {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.invoice-journal-entry:hover {
  background-color: hsl(var(--color-80));
}

.invoice-journal__cell:last-child, .invoice-journal__cell:nth-last-child(2) {
  text-align: right;
}

.invoice-lines {
  margin-top: 20px;
  
  display: grid;
  grid-template-columns: max-content max-content auto repeat(5, max-content);
  column-gap: 10px;
}

.invoice-lines__header {
  display: contents;
  font-size: 12px;
}

.invoice-lines__header :deep(.invoice-line__cell:nth-child(1)) {
  padding-left: 5px;
}

.invoice-lines__header :deep(.invoice-line__cell:last-child) {
  padding-right: 7px;
}

:deep(.invoice-line__cell:nth-child(1)),
:deep(.invoice-line__cell:nth-child(4)),
:deep(.invoice-line__cell:nth-child(5)),
:deep(.invoice-line__cell:nth-child(6)) {
  text-align: right;
}

.invoice-line__description {
  grid-column: 3 / 5;
}

.invoice-lines__lines {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: subgrid;
}

.invoice-summary {
  display: grid;
  grid-template-columns: auto max-content;
  column-gap: 10px;
  text-align: right;
  padding: 0 5px;
  margin-top: 5px;
}

.invoice-summary__label {
  text-align: right;
}

.invoice-summary__total {
  font-weight: bold;
  font-size: 16px;
}

@container (max-width: 650px) {
  .invoice-details {
    grid-template-columns: auto;
    grid-auto-flow: row;
  }
}
</style>